// Practice loop for the Synth Lab: a structured "Drill" of Spot-the-Difference
// ear-training rounds, plus the progress/stats engine that tracks how you're
// doing per concept (which knobs your ear is strong/weak on).
//
// Everything here is pure data + pure functions (no audio, no React, no
// storage) so the round generation, scoring and stat math are all unit-tested.
// The page owns playback and localStorage; this module owns the logic.

import {
  PARAMS,
  PARAM_BY_ID,
  WAVEFORM_VALUES,
  clamp,
  normalizeParam,
  denormalizeParam,
  randomPatch,
} from "./synthLab";

/* ============================================================
   Concepts — the trackable skills (the six knobs + waveform)
   ============================================================ */

export const CONCEPTS = [
  ...PARAMS.map((p) => ({ id: p.id, label: p.label })),
  { id: "waveform", label: "Waveform" },
];
const CONCEPT_IDS = CONCEPTS.map((c) => c.id);
const CONCEPT_ID_SET = new Set(CONCEPT_IDS);

export const conceptLabel = (id) =>
  id === "waveform" ? "Waveform" : PARAM_BY_ID[id] ? PARAM_BY_ID[id].label : id;

// Concise, opposite labels for a "which way did it move?" question. Phrased in
// musical terms (not just "up/down") so the drill teaches the vocabulary too.
export const DIRECTION_LABELS = {
  cutoff: { up: "Brighter", down: "Darker" },
  resonance: { up: "More resonant", down: "Less resonant" },
  attack: { up: "Slower attack", down: "Faster attack" },
  decay: { up: "Longer decay", down: "Shorter decay" },
  sustain: { up: "Higher sustain", down: "Lower sustain" },
  release: { up: "Longer tail", down: "Shorter tail" },
};

/* ============================================================
   Progress / stats — a pure, serializable record of attempts
   ============================================================ */

// A fresh progress record: per-concept tallies plus a running + best streak.
export const emptyProgress = () => ({
  byConcept: {}, // id → { attempts, correct }
  totalAttempts: 0,
  totalCorrect: 0,
  streak: 0,
  best: 0,
});

// Fold one answer into the progress record, returning a NEW record (so React
// state updates stay immutable). A miss resets the streak; `best` only grows.
export const recordAttempt = (progress, concept, correct) => {
  const prev = progress.byConcept[concept] || { attempts: 0, correct: 0 };
  const streak = correct ? progress.streak + 1 : 0;
  return {
    byConcept: {
      ...progress.byConcept,
      [concept]: {
        attempts: prev.attempts + 1,
        correct: prev.correct + (correct ? 1 : 0),
      },
    },
    totalAttempts: progress.totalAttempts + 1,
    totalCorrect: progress.totalCorrect + (correct ? 1 : 0),
    streak,
    best: Math.max(progress.best, streak),
  };
};

// Per-concept breakdown in CONCEPTS order, each with accuracy (null = untried).
export const conceptStats = (progress) =>
  CONCEPTS.map(({ id, label }) => {
    const c = progress.byConcept[id] || { attempts: 0, correct: 0 };
    return {
      id,
      label,
      attempts: c.attempts,
      correct: c.correct,
      accuracy: c.attempts ? c.correct / c.attempts : null,
    };
  });

// Overall accuracy as a 0..1 fraction, or null before any attempts.
export const overallAccuracy = (progress) =>
  progress.totalAttempts ? progress.totalCorrect / progress.totalAttempts : null;

// The concept your ear is weakest on: lowest accuracy among those tried at
// least `minAttempts` times (so a single unlucky miss doesn't flag it). Ties
// break toward the most-attempted. Returns null when there isn't enough data.
export const weakestConcept = (progress, minAttempts = 3) => {
  const eligible = conceptStats(progress).filter((c) => c.attempts >= minAttempts);
  if (!eligible.length) return null;
  eligible.sort((a, b) => a.accuracy - b.accuracy || b.attempts - a.attempts);
  return eligible[0].id;
};

// Validate/repair a record loaded from localStorage (which a user could have
// edited, or which could be from an older shape). Totals are recomputed from
// the per-concept tallies so they can never drift out of sync.
export const coerceProgress = (raw) => {
  if (!raw || typeof raw !== "object") return emptyProgress();
  const byConcept = {};
  if (raw.byConcept && typeof raw.byConcept === "object") {
    Object.entries(raw.byConcept).forEach(([id, v]) => {
      if (!CONCEPT_ID_SET.has(id) || !v || typeof v !== "object") return;
      const attempts = Number.isFinite(v.attempts) ? Math.max(0, Math.floor(v.attempts)) : 0;
      const correct = Number.isFinite(v.correct) ? clamp(Math.floor(v.correct), 0, attempts) : 0;
      if (attempts > 0) byConcept[id] = { attempts, correct };
    });
  }
  const totalAttempts = Object.values(byConcept).reduce((s, v) => s + v.attempts, 0);
  const totalCorrect = Object.values(byConcept).reduce((s, v) => s + v.correct, 0);
  const streak = Number.isFinite(raw.streak) ? Math.max(0, Math.floor(raw.streak)) : 0;
  const best = Number.isFinite(raw.best) ? Math.max(0, Math.floor(raw.best)) : 0;
  return { byConcept, totalAttempts, totalCorrect, streak, best: Math.max(best, streak) };
};

/* ============================================================
   Spot-the-Difference rounds — A vs B, one control apart
   ============================================================ */

// Difficulty bands. A bigger normalized `shift` is more obvious (easier); more
// `choices` (for "identify" rounds) is harder. Levels ramp across a drill run.
const LEVELS = {
  1: { shift: [0.55, 0.75], choices: 4 },
  2: { shift: [0.38, 0.55], choices: 5 },
  3: { shift: [0.24, 0.4], choices: 6 },
};

const pickFrom = (arr, rng) => arr[Math.floor(rng() * arr.length)];

// In-place Fisher–Yates using the injected rng, so shuffles are deterministic
// under a seeded generator (Array.sort(()=>rng()-0.5) is biased + unstable).
const shuffle = (arr, rng) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = arr[i];
    arr[i] = arr[j];
    arr[j] = t;
  }
  return arr;
};

// Move one control of `base` by a clearly-audible amount, away from whichever
// end it's nearer so the direction of the change is unambiguous. Returns the
// modified patch and the human direction ("up"/"down", or null for waveform).
const shiftParam = (base, id, mag, rng) => {
  const b = { ...base };
  if (id === "waveform") {
    b.waveform = pickFrom(WAVEFORM_VALUES.filter((w) => w !== base.waveform), rng);
    return { patch: b, direction: null };
  }
  const n = normalizeParam(id, base[id]);
  const dir = n > 0.5 ? -1 : 1;
  const next = clamp(n + dir * mag, 0.05, 0.95);
  b[id] = denormalizeParam(id, next);
  return { patch: b, direction: next > n ? "up" : "down" };
};

// Build a single round: two patches that differ in `controls` control(s).
//   type "identify"  → "which control(s) changed?" (multiple choice of labels)
//   type "direction" → "the X changed, which way?" (its two opposite labels)
// `changedId` forces one control to move (used to target a weak concept);
// otherwise they're random. Direction rounds are always single-control (asking
// the direction of two at once is ambiguous) and never use waveform (no up/down).
// `controls` (1 or 2) drives the multi-control difficulty toggle.
export const makeSpotRound = (
  rng = Math.random,
  { level = 1, type = "identify", changedId = null, controls = 1 } = {}
) => {
  const lv = LEVELS[clamp(level, 1, 3)] || LEVELS[1];
  const a = randomPatch(rng);
  const pool = type === "direction" ? PARAMS.map((p) => p.id) : CONCEPT_IDS;
  const n = type === "direction" ? 1 : clamp(Math.round(controls), 1, 2);

  // Choose `n` distinct controls to move (honoring a forced one first).
  const changedIds = [];
  if (changedId && pool.includes(changedId)) changedIds.push(changedId);
  const rest = shuffle(pool.filter((x) => !changedIds.includes(x)), rng);
  while (changedIds.length < n) changedIds.push(rest.pop());

  // Apply each shift in turn (controls are distinct params, so they accumulate).
  let b = a;
  const directions = {};
  changedIds.forEach((id) => {
    const mag = lv.shift[0] + rng() * (lv.shift[1] - lv.shift[0]);
    const res = shiftParam(b, id, mag, rng);
    b = res.patch;
    directions[id] = res.direction;
  });

  // identify choices: the real control(s) plus distractors, shuffled.
  const distractors = shuffle(CONCEPT_IDS.filter((x) => !changedIds.includes(x)), rng);
  const count = Math.min(lv.choices, CONCEPT_IDS.length);
  const choices = shuffle(
    [...changedIds, ...distractors.slice(0, Math.max(0, count - changedIds.length))],
    rng
  );

  return {
    type,
    a,
    b,
    changedIds,
    changedId: changedIds[0], // convenience for single-control consumers
    direction: directions[changedIds[0]],
    directions,
    choices,
    level: clamp(level, 1, 3),
  };
};

// How many rounds a drill session runs.
export const DRILL_LENGTH = 8;

// A full drill session: a gentle difficulty ramp (level 1→2→3 in thirds) and an
// alternating mix of identify/direction questions. When `focusConcept` is set
// (e.g. the player's weakest), every other round targets it — turning the drill
// into targeted practice on whatever needs work. When `multi` is on, the harder
// identify rounds (level 2+) move TWO controls at once, so the player can work
// up to spotting two changes; level-1 rounds stay single as a gentle warm-up.
export const makeDrillRun = (
  rng = Math.random,
  { length = DRILL_LENGTH, focusConcept = null, multi = false } = {}
) => {
  const rounds = [];
  for (let i = 0; i < length; i++) {
    const level = i < length / 3 ? 1 : i < (2 * length) / 3 ? 2 : 3;
    const type = i % 2 === 1 ? "direction" : "identify";
    let changedId = focusConcept && i % 2 === 0 ? focusConcept : null;
    // waveform has no direction, so it can't anchor a direction round.
    if (type === "direction" && changedId === "waveform") changedId = null;
    const controls = multi && type === "identify" && level >= 2 ? 2 : 1;
    rounds.push(makeSpotRound(rng, { level, type, changedId, controls }));
  }
  return rounds;
};

// The set of correct concept ids for an identify round (1 or 2 controls).
export const roundAnswerKey = (round) => round.changedIds || [round.changedId];

// Whether an answer to a round is correct. For direction rounds `answer` is
// "up"/"down". For identify rounds it's a single concept id (single-control) or
// an array of ids (multi-control) — which must match the changed set exactly.
export const isRoundCorrect = (round, answer) => {
  if (round.type === "direction") return answer === round.direction;
  const key = roundAnswerKey(round);
  if (Array.isArray(answer)) {
    return answer.length === key.length && key.every((id) => answer.includes(id));
  }
  return key.length === 1 && answer === key[0];
};
